import type RoomObsolete from './room/room-obsolete';
import type PermittedAdmin from './permitted-admin';
import type PermittedUser from './permitted-user';
import type Department from './department/department';
import type PermittedUserAmount from './permitted-user-amount';
import type NotificationSettings from './notification-settings';
import type AgendaEntry from '../../statistics/models/agenda-entry';
import { defaultBookingTimeZone, defaultMaxCapacity, defaultPlanPeriodDays } from './office.constants';

class Office {
  id?: string;

  createdAt: Date = new Date();

  name = '';

  companyId?: string;

  /**
   * Only populated by `office/by-officemanager-or-admin`. The company endpoints are Whyellow-only,
   * so this is the one place an administrator or office manager can learn their company's name.
   */
  companyName?: string;

  authorEmail = '';

  officeId = '';

  planPeriodDays: number = defaultPlanPeriodDays;

  /**
   * IANA timezone identifier (e.g. 'Europe/Amsterdam') deciding which calendar day
   * is "today" for this office, and therefore where the planPeriodDays booking
   * window starts and ends.
   */
  bookingTimeZone: string = defaultBookingTimeZone;

  preferredCapacityPercentage: number = defaultMaxCapacity;

  lunchEnabled = false;

  webUrl = '';

  webLabel = '';

  permittedUsers: PermittedUser[] = [];

  permittedAdmins: PermittedAdmin[] = [];

  monthlyHighestPermittedUserAmounts: PermittedUserAmount[] = [];

  departments: Department[] = [];

  rooms: RoomObsolete[] = [];

  notificationSettings?: NotificationSettings;

  agendaEntries: AgendaEntry[] = [];

  qrCodesEnabled = false;

  floorPlanEnabled = false;

  isFloorPlanAvailable = false;

  isMaxCapacityTheLimit = false;

  groupReservationsEnabled = false;

  maximumGroupSize?: number;

  /**
   * How many leaders one of this office's groups may have at once, including the person who
   * created it. Undefined leaves the office on the API's default.
   */
  maximumGroupLeaders?: number;

  /**
   * How many days ahead a group leader may book a group reservation. Undefined means the same as
   * planPeriodDays. Only kept while group reservations are on.
   */
  groupPlanPeriodDays?: number;

  isAvailableInApp = false;

  cateringNotificationEnabled = false;

  cateringNotificationEmailSubject = '';

  cateringNotificationEmailBody = '';

  cateringNotificationRecipients = '';

  cateringNotificationSendTime = '';

  /** IANA timezone identifier for the send time (e.g. 'Europe/Amsterdam'). */
  cateringNotificationTimeZone = '';

  cateringNotificationDaysAhead?: number;

  // Occupancy-gauge override: "feels empty below" / "feels overcrowded above". Not set means
  // inherit the level above. The API sends an unset threshold as JSON null rather than omitting
  // it, so both null and undefined occur at runtime — use isThresholdPairValid, never === undefined.
  thresholdMinPercentage?: number | null;

  thresholdMaxPercentage?: number | null;
}

export default Office;
